// Top-level May 2026 evaluation wizard.
//
// Phase A (2026-05-29): added Welcome screen + word-by-word probe rework.
// Phase B (2026-05-30): mode question relocated to AFTER probes per V3
// framework §4, submitted via PATCH /api/evaluations/:id { childPresent }
// instead of bundling on the first probe payload.
//
// State machine:
//   welcome → idle → questions → transition → probes → mode_question → saving → done
//                                                                              ↘ error
//
// Backend contract (see May2026_01_Evaluation_BE.md):
//   POST /api/evaluations             → starts a session, returns Q1
//   POST /api/evaluations/:id/answer  → discriminated phase response:
//       - core_questions  → next Q
//       - transition      → first probe prefetched alongside transition copy
//       - probes          → next probe
//       - complete        → final EvaluationResults
//   PATCH /api/evaluations/:id        → sets childPresent on a completed eval
//                                       (returns reconstructed EvaluationResults)

import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Loader2, X, AlertCircle } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useGlobalState } from '@/stores/globalState'
import { capture } from '@/lib/analytics'
import { Grain } from '@/components/hs/Grain'
import { setChildPresent, startEvaluation, submitAnswer } from '@/lib/api/evaluations'
import { EvaluationQuestionV2 } from '@/modules/evaluation/EvaluationQuestionV2'
import { AnalyzingScreen } from '@/modules/evaluation/AnalyzingScreen'
import { EvaluationResultsV2View } from '@/modules/evaluation/EvaluationResultsV2View'
import { ProbeOrchestrator } from '@/modules/evaluation/ProbeOrchestrator'
import { WelcomeScreen } from '@/modules/evaluation/screens/WelcomeScreen'
import { TransitionScreen } from '@/modules/evaluation/screens/TransitionScreen'
import { ModeQuestionScreen } from '@/modules/evaluation/screens/ModeQuestionScreen'
import type {
  AnswerResponse,
  EvalQuestionV2,
  EvaluationResults,
  EvaluationResultsV2,
  ProbeDefinition,
} from '@/types'
import { PH_UNMASK, MaskedName } from '@/lib/privacyMask'

type WizardStep =
  | 'welcome'
  | 'idle'           // brief loading state after Get-started while startEvaluation runs
  | 'questions'
  | 'transition'
  | 'mode_question'
  | 'probes'
  | 'saving'
  | 'done'
  | 'error'

interface EvaluationWizardProps {
  onComplete?: (result: EvaluationResultsV2) => void
  onClose?: () => void
  showCloseButton?: boolean
  // When used as the mandatory placement gate (un-evaluated learner landing
  // on Home / Books / Profile), the welcome step fills the panel edge-to-edge
  // with no gutters, matching the full-bleed "done" report.
  fullBleedWelcome?: boolean
  // Skip the welcome step and start the evaluation immediately. For callers
  // whose CTA already collected intent ("Start the placement" on the new-
  // learner card, "Retake" on the report) — the welcome would be a second
  // ask and reads as "the placement didn't start". Organic gate landings
  // (Home/Books/Profile) keep the welcome: a parent wandering in shouldn't
  // silently open an evaluation session (framework §10 deferred start).
  autoStart?: boolean
}

export function EvaluationWizard({
  onComplete,
  onClose,
  showCloseButton = true,
  fullBleedWelcome = false,
  autoStart = false,
}: EvaluationWizardProps) {
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const updateLearner = useGlobalState((s) => s.updateLearner)
  const queryClient = useQueryClient()
  const learnerName = activeLearner?.name ?? 'them'

  const [step, setStep] = useState<WizardStep>(autoStart ? 'idle' : 'welcome')
  const [evaluationId, setEvaluationId] = useState<string | null>(null)
  const [currentQuestion, setCurrentQuestion] = useState<EvalQuestionV2 | null>(null)
  const [pendingProbe, setPendingProbe] = useState<ProbeDefinition | null>(null)
  const [activeProbe, setActiveProbe] = useState<ProbeDefinition | null>(null)
  // DEP-178: has the learner reached a printed-word probe yet? Gates the mode
  // question — a pre-reader who fails the oral sound check never sees printed
  // words, so the "was [child] reading these words" question is skipped.
  const [sawWordProbe, setSawWordProbe] = useState(false)
  // childPresent state removed in Phase B — captured AFTER probes via the
  // mode_question step and PATCHed directly to the BE. No need to thread
  // through probe submissions.
  const [answeredCount, setAnsweredCount] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [finalResult, setFinalResult] = useState<EvaluationResultsV2 | null>(null)

  const startedRef = useRef(false)

  // ── Start the evaluation when user clicks Get-started on Welcome ─────
  // Per the May 2026 framework §10 + the compliance plan: defer the API call
  // until the user confirms intent (avoids idle evaluations + matches the
  // wizard state machine where 'welcome' is the first step).
  const start = useCallback(async () => {
    if (!activeLearner || startedRef.current) return
    startedRef.current = true
    setError(null)
    setSawWordProbe(false)
    setStep('idle')
    capture('placement_started')
    try {
      const data = await startEvaluation(activeLearner.id)
      setEvaluationId(data.evaluationId)
      setCurrentQuestion(data.nextQuestion)
      setStep('questions')
    } catch (err) {
      console.error('[EvaluationWizard] startEvaluation failed:', err)
      setError('Failed to start the evaluation. Please try again.')
      setStep('error')
      startedRef.current = false   // allow retry
    }
  }, [activeLearner])

  // ── Auto-start (intent already collected by the caller's CTA) ─────────
  // Fires once on mount; startedRef makes re-runs (StrictMode, activeLearner
  // settling in) no-ops. The microtask defers start()'s state writes out of
  // the effect body. The initial step is already 'idle', so there's no
  // welcome flash.
  useEffect(() => {
    if (!autoStart) return
    queueMicrotask(() => {
      void start()
    })
  }, [autoStart, start])

  // ── Apply an /answer response by routing to the next step ─────────────
  const applyResponse = useCallback(
    (response: AnswerResponse) => {
      setAnsweredCount((c) => c + 1)
      switch (response.phase) {
        case 'core_questions':
          setCurrentQuestion(response.nextQuestion)
          setStep('questions')
          break
        case 'transition':
          // After the 3 questions the BE prefetches the first probe.
          // DEP-178 Req 1: a pre-reader's first probe is the oral SOUND CHECK,
          // which shows no printed words — so DON'T show the "we'll show some
          // words" transition before it; go straight to the sound check.
          // Readers (first probe is word-reading) still see the transition.
          if (response.nextProbe.type === 'sound_check') {
            setActiveProbe(response.nextProbe)
            setStep('probes')
          } else {
            setPendingProbe(response.nextProbe)
            setStep('transition')
          }
          break
        case 'probes':
          // A subsequent probe. DEP-178 Req 2: when a pre-reader passes the
          // sound check and advances to the first printed-word probe, show the
          // transition screen at THAT point (right before the first word).
          // Otherwise render the next probe directly.
          if (activeProbe?.type === 'sound_check' && response.nextProbe.type === 'word_reading') {
            setPendingProbe(response.nextProbe)
            setStep('transition')
          } else {
            if (response.nextProbe.type === 'word_reading') setSawWordProbe(true)
            setActiveProbe(response.nextProbe)
            setStep('probes')
          }
          break
        case 'complete':
          capture('placement_completed', {
            final_stage: response.result.finalStage,
            stage_label: response.result.stageLabel,
          })
          // Persist the result onto the local activeLearner so the rest of
          // the app (gates, gallery banners, etc.) reflects the new state
          // without waiting for a refetch. The V2 shape is wider than the
          // legacy EvaluationResults — cast at the boundary; extra fields
          // are preserved at runtime.
          // NOTE: do NOT set evaluationComplete here. The Home/Books placement
          // gate (ModuleRouter) keys on that flag alone; flipping it now would
          // unmount this wizard before it renders mode_question → saving → done,
          // bouncing the user to Home with the results never shown. We mirror
          // the result data (for banners / the results surface) but defer the
          // completion flag to handleResultsDismiss, so the gate stays open
          // until the user has actually read the report.
          updateLearner({
            readingStage: response.result.finalStage,
            stageLabel: response.result.stageLabel,
            lastEvaluationDate: response.result.completedAt,
            evaluationResults:
              response.result as unknown as EvaluationResults,
          })
          // Invalidate the React Query cache so any subsequent fetch picks
          // up the BE-persisted shape as the source of truth.
          queryClient.invalidateQueries({ queryKey: ['learners'] })
          queryClient.invalidateQueries({
            queryKey: ['learner', activeLearner?.id],
          })
          setFinalResult(response.result)
          // The mode question runs after probes, before saving — BUT only if
          // the learner actually saw printed words. DEP-178 Req 3: a pre-reader
          // who fails the sound check never sees words, so "was [child] reading
          // these words" is meaningless. Skip it, auto-set childPresent=false,
          // and go straight to saving. (childPresent is metadata-only and does
          // not affect placement, so the background PATCH never blocks.)
          if (sawWordProbe) {
            setStep('mode_question')
          } else {
            setStep('saving')
            if (evaluationId) {
              void setChildPresent(evaluationId, false)
                .then((updated) => {
                  setFinalResult(updated)
                  updateLearner({
                    evaluationResults: updated as unknown as EvaluationResults,
                  })
                  queryClient.invalidateQueries({ queryKey: ['learners'] })
                  queryClient.invalidateQueries({
                    queryKey: ['learner', activeLearner?.id],
                  })
                })
                .catch((err) =>
                  console.error('[EvaluationWizard] setChildPresent(false) failed:', err),
                )
            }
          }
          break
      }
    },
    [updateLearner, queryClient, activeLearner?.id, activeProbe?.type, evaluationId, sawWordProbe],
  )

  // ── Q1/Q2/Q3 answer handler ──────────────────────────────────────────
  const handleQuestionAnswer = useCallback(
    async (questionId: string, answer: string | string[]) => {
      if (!evaluationId || submitting) return
      setSubmitting(true)
      try {
        const response = (await submitAnswer(evaluationId, {
          questionId,
          answer,
          // Discriminated union narrows on the answer type — cast here is fine.
        } as Parameters<typeof submitAnswer>[1])) as AnswerResponse
        applyResponse(response)
      } catch (err) {
        console.error('[EvaluationWizard] submitAnswer failed:', err)
        setError('Something went wrong submitting your answer. Please try again.')
        setStep('error')
      } finally {
        setSubmitting(false)
      }
    },
    [evaluationId, submitting, applyResponse],
  )

  // ── Transition → first probe ─────────────────────────────────────────
  // Phase B (2026-05-30): mode question is no longer in this position. On
  // continue, the prefetched first probe takes over directly.
  const handleTransitionContinue = () => {
    if (pendingProbe) {
      // The transition now always precedes a printed-word probe (a reader's
      // first probe, or a pre-reader who passed the sound check). Record that
      // words were shown so the mode question runs after probes (DEP-178).
      if (pendingProbe.type === 'word_reading') setSawWordProbe(true)
      setActiveProbe(pendingProbe)
      setPendingProbe(null)
    }
    setStep('probes')
  }

  // ── Mode question handler (runs AFTER probes complete) ───────────────
  // Per V3 framework §4: probes finish → mode question → saving. Submits
  // childPresent via PATCH /api/evaluations/:id and folds the response back
  // into local state so the V2 view + learner record reflect the new value.
  const handleModeQuestionAnswered = async (present: boolean) => {
    if (!evaluationId || !finalResult) {
      // Defensive — shouldn't happen if the wizard reached this step. Skip
      // the API call and advance so the user isn't stuck.
      setStep('saving')
      return
    }
    setSubmitting(true)
    try {
      const updated = await setChildPresent(evaluationId, present)
      setFinalResult(updated)
      // Mirror into local activeLearner so other parts of the app pick up
      // the now-non-null childPresent without waiting for a refetch.
      updateLearner({
        evaluationResults: updated as unknown as EvaluationResults,
      })
      queryClient.invalidateQueries({ queryKey: ['learners'] })
      queryClient.invalidateQueries({
        queryKey: ['learner', activeLearner?.id],
      })
      setStep('saving')
    } catch (err) {
      console.error('[EvaluationWizard] setChildPresent failed:', err)
      // Don't block completion — childPresent is metadata-only and doesn't
      // affect placement. Surface a warning in console and advance.
      // (The eval is already persisted; the user has their result.)
      setStep('saving')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Probe response handler (from ProbeOrchestrator) ──────────────────
  // Phase B: no childPresent bundling concern anymore — just forward.
  const handleProbeResponse = (response: AnswerResponse) => {
    applyResponse(response)
  }

  const handleProbeError = (err: unknown) => {
    console.error('[EvaluationWizard] probe submission failed:', err)
    setError('Something went wrong submitting that probe. Please try again.')
    setStep('error')
  }

  // ── Saving → done handoff ────────────────────────────────────────────
  // After the 1.5s AnalyzingScreen, advance to the inline results view.
  // We do NOT call onComplete here — that fires only when the user dismisses
  // the results screen (so they have a chance to read it).
  const handleSavingComplete = () => {
    if (!finalResult) return
    setStep('done')
  }

  // Fired when the user clicks Continue on the results screen. This is where
  // the learner actually flips to "evaluated": deferring it here (rather than
  // at the 'complete' phase) keeps the placement gate open so the results
  // render, then closes it as the user leaves for the report/gallery.
  const handleResultsDismiss = () => {
    updateLearner({ evaluationComplete: true })
    if (finalResult) onComplete?.(finalResult)
  }

  // ── Retry from error ─────────────────────────────────────────────────
  // If we never got an evaluationId, the failure was in startEvaluation —
  // restart from scratch. Otherwise the failure was a transient /answer
  // failure; nudge the user back to the step they were on (they re-submit).
  const handleRetry = () => {
    if (!evaluationId) {
      startedRef.current = false
      setError(null)
      start()
      startedRef.current = true
      return
    }
    setError(null)
    if (activeProbe) {
      setStep('probes')
    } else if (pendingProbe) {
      setStep('transition')
    } else if (currentQuestion) {
      setStep('questions')
    } else {
      // Lost state — start over.
      startedRef.current = false
      setEvaluationId(null)
      start()
      startedRef.current = true
    }
  }

  // ── Render ───────────────────────────────────────────────────────────

  if (!activeLearner) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
        Select a learner to begin.
      </div>
    )
  }

  return (
    <div
      {...PH_UNMASK}
      className={
        step === 'done' || (fullBleedWelcome && step === 'welcome')
          ? // the placement report (and the mandatory-gate welcome) are
            // full-bleed keepsakes — no gutters
            'hs-brand relative flex flex-1 flex-col'
          : 'hs-brand relative flex flex-1 flex-col items-center px-4 py-8'
      }
    >
      <Grain />
      {showCloseButton && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card shadow-sm hover:bg-muted"
          aria-label="Close placement"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Stamp-square progress during the parent's Q1–Q3 (HOP-23) */}
      {step === 'questions' && (
        <CoreQuestionProgress
          answeredCount={Math.min(answeredCount, 3)}
          learnerName={learnerName}
        />
      )}

      <div
        className={
          step === 'done'
            ? 'flex w-full flex-1 flex-col'
            : 'mt-6 flex w-full flex-1 items-center justify-center'
        }
      >
        {step === 'welcome' && (
          <WelcomeScreen
            learnerName={learnerName}
            onStart={start}
            submitting={submitting}
          />
        )}

        {step === 'idle' && (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Getting the placement ready…</p>
          </div>
        )}

        {step === 'error' && (
          <div className="flex max-w-md flex-col items-center gap-4 text-center">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleRetry}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                Try again
              </button>
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        )}

        {step === 'questions' && currentQuestion && (
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion.id}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="w-full max-w-[600px]"
            >
              <EvaluationQuestionV2
                question={currentQuestion}
                onAnswer={handleQuestionAnswer}
                submitting={submitting}
              />
            </motion.div>
          </AnimatePresence>
        )}

        {step === 'transition' && (
          <TransitionScreen
            learnerName={learnerName}
            onContinue={handleTransitionContinue}
          />
        )}

        {step === 'mode_question' && (
          <ModeQuestionScreen
            learnerName={learnerName}
            onAnswer={handleModeQuestionAnswered}
            submitting={submitting}
          />
        )}

        {step === 'probes' && activeProbe && evaluationId && (
          <ProbeOrchestrator
            evaluationId={evaluationId}
            probe={activeProbe}
            learnerName={learnerName}
            onResponse={handleProbeResponse}
            onError={handleProbeError}
          />
        )}

        {step === 'saving' && (
          <AnalyzingScreen
            learnerName={learnerName}
            onComplete={handleSavingComplete}
          />
        )}

        {step === 'done' && finalResult && (
          <EvaluationResultsV2View
            result={finalResult}
            learnerName={learnerName}
            onDone={handleResultsDismiss}
          />
        )}
      </div>
    </div>
  )
}

// ── Stamp-square progress for Q1/Q2/Q3 (HOP-23) ────────────────────────
// Ink = done, coral = current (you-are-here), outline = upcoming.

function CoreQuestionProgress({
  answeredCount,
  learnerName,
}: {
  answeredCount: number
  learnerName: string
}) {
  return (
    <div className="core-question-progress">
      <div className="core-question-progress__stones">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`core-question-progress__stone ${
              i < answeredCount
                ? 'is-done'
                : i === answeredCount
                  ? 'is-current'
                  : 'is-upcoming'
            }`}
          />
        ))}
      </div>
      <span className="hs-eyebrow core-question-progress__label">
        About <MaskedName>{learnerName}</MaskedName> · {Math.min(answeredCount + 1, 3)} of 3
      </span>
    </div>
  )
}
